import * as Dialog from '@radix-ui/react-dialog'
import { AnimatePresence, LazyMotion, m, domAnimation } from 'framer-motion'
import * as React from 'react'
import { tw } from 'utils/classnames'

import { PortalContainerProvider } from '../portal/portal-container'

import { CloseButton } from '../buttons/close-button'
import { useScrollbarWidth } from './hooks/use-scrollbar-width'

// eslint-disable-next-line no-barrel-files/no-barrel-files
// oxlint-disable-next-line cf-web/no-barrel-file
export * from '@radix-ui/react-dialog'

const DialogContext = React.createContext<{
  open: boolean
  onOpenChange: (open: boolean) => void
  handleClose: () => void
}>(null!)

export function useDialog() {
  const context = React.useContext(DialogContext)

  if (!context) {
    throw new Error('useDialog must be used within a Dialog')
  }

  return context
}

export function Root({
  open: openControlled,
  onOpenChange: onOpenChangeControlled,
  defaultOpen,
  children,
  modal = true, // Radix dialog default is true
}: {
  id?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultOpen?: boolean
  children: React.ReactNode
  modal?: boolean
}) {
  const [open, onOpenChange] = React.useState(defaultOpen ?? false)

  const isControlled = openControlled !== undefined && onOpenChangeControlled !== undefined

  const value = React.useMemo(
    () => ({
      open: isControlled ? openControlled : open,
      onOpenChange: isControlled ? onOpenChangeControlled : onOpenChange,
      handleClose: () => (isControlled ? onOpenChangeControlled(false) : onOpenChange(false)),
    }),
    [isControlled, onOpenChangeControlled, open, openControlled],
  )

  return (
    <DialogContext.Provider value={value}>
      <Dialog.Root
        open={value.open}
        // oxlint-disable-next-line react/jsx-handler-names
        onOpenChange={value.onOpenChange}
        modal={modal}
        defaultOpen={value.open}
      >
        {children}
      </Dialog.Root>
    </DialogContext.Provider>
  )
}

export function CloseX(props: React.ComponentProps<typeof CloseButton>) {
  return (
    <Dialog.Close asChild>
      <CloseButton
        {...props}
        className={tw('absolute right-2 top-2 text-gray-400', props.className)}
      />
    </Dialog.Close>
  )
}

export function Footer({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={tw('xl:-mb-8 xl:-mx-8', 'lg:-mb-6 lg:-mx-6', '-mb-4 -mx-4', className)}>
      {children}
    </div>
  )
}

function BodyRoot({
  zIndex,
  overlayZIndex,
  dismissable = true,
  initial = true,
  children,
  className,
  placement = 'middle',
  overlayClassnames,
  overlayStyle,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof Dialog.Content> & {
  dismissable?: boolean
  zIndex?: number
  overlayZIndex?: number
  initial?: boolean
  placement?: 'middle' | 'bottom' | 'top'
  overlayClassnames?: string
  overlayStyle?: React.CSSProperties
  ref?: React.Ref<HTMLDivElement>
}) {
  const { open, onOpenChange } = useDialog()

  const portalContainerRef = React.useRef<HTMLDivElement>(null)
  const pressedBackdropRef = React.useRef(false)
  const scrollbarWidth = useScrollbarWidth()

  function setContentRef(node: HTMLDivElement | null) {
    portalContainerRef.current = node

    if (typeof ref === 'function') {
      ref(node)
      return
    }

    if (ref) {
      ref.current = node
    }
  }

  /**
   * Capture phase, so it still records the press when a descendant stops propagation.
   *
   * A `click` alone cannot tell us the backdrop was clicked: it is dispatched at the nearest common
   * ancestor of its pointerdown and pointerup targets, and at the nearest surviving ancestor when
   * its own target unmounts mid-interaction — so this element becomes the click target whenever a
   * press starts on one descendant and ends on another. Poppers rendered in here do exactly that:
   * opening a select on pointerdown puts its listbox under the cursor before pointerup, and picking
   * an item unmounts that item.
   */
  function onPointerDownCapture(event: React.PointerEvent<HTMLDivElement>) {
    pressedBackdropRef.current = event.target === event.currentTarget
  }

  function onCloseFromContentOverlay(event: React.MouseEvent<HTMLDivElement>) {
    if (dismissable && pressedBackdropRef.current && event.target === event.currentTarget) {
      onOpenChange(false)
    }
  }

  function onEscapeKeyDown(event: KeyboardEvent) {
    if (!dismissable) {
      event.preventDefault()
    }
  }

  React.useEffect(() => {
    if (open) {
      document.body.style.setProperty('--removed-body-scroll-bar-size', `${scrollbarWidth}px`)
    } else {
      document.body.style.overflow = ''
      document.body.style.removeProperty('--removed-body-scroll-bar-size')
    }
  }, [open, scrollbarWidth])

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence initial={initial}>
        {open ? (
          <m.div
            initial={{
              opacity: 0,
            }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { ease: 'easeOut', duration: 0.15 } }}
            style={{ zIndex }} // I have added this line because in cf-main nav is 9999 =(
            key="overlay"
            className="relative"
          >
            {/* bg colour is hard coded so it's the same value on dark and light mode */}
            <Dialog.Overlay
              className={tw('fixed inset-0 bg-[#272B2F]/80', overlayClassnames)}
              style={{ zIndex: overlayZIndex, ...overlayStyle }}
            />
          </m.div>
        ) : null}
        {open ? (
          <Dialog.Content
            key="content"
            asChild
            forceMount
            onEscapeKeyDown={onEscapeKeyDown}
            onPointerDownCapture={onPointerDownCapture}
            onClick={onCloseFromContentOverlay}
            ref={setContentRef}
            onCloseAutoFocus={event => {
              event.preventDefault()
              document.body.style.pointerEvents = ''
            }}
            {...props}
          >
            <m.div
              initial={{ opacity: 0, scale: 0.975 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9875, transition: { ease: 'easeOut', duration: 0.2 } }}
              className={tw(
                'fixed inset-0 flex h-[100dvh] w-screen justify-center outline-none',
                className,
                (!placement || placement === 'middle') && 'items-center',
                placement === 'top' && 'items-start',
                placement === 'bottom' && 'items-end',
              )}
              style={{ zIndex: zIndex ? zIndex + 1 : undefined }} // I have added this line because in cf-main nav is 9999 =(
            >
              <PortalContainerProvider containerRef={portalContainerRef}>
                <Dialog.Title className="hidden" />
                {children}
              </PortalContainerProvider>
            </m.div>
          </Dialog.Content>
        ) : null}
      </AnimatePresence>
    </LazyMotion>
  )
}

export const Body = BodyRoot
